import React, { useState } from 'react';
import { ErrorBoundaryTest } from '@/shared/ui/components/ErrorBoundary/ErrorBoundaryTest';
import { Button, Modal, ConfirmModal } from '@/shared/ui';
import { logger } from '@/shared/lib';

export const TestErrorPage: React.FC = () => {
  const [showErrorComponent, setShowErrorComponent] = useState(false);
  
  // Modal states
  const [isBasicModalOpen, setIsBasicModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isLoadingConfirmOpen, setIsLoadingConfirmOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);

  // Modal handlers
  const handleConfirm = () => {
    alert('확인 버튼이 클릭되었습니다!');
    setIsConfirmModalOpen(false);
  };

  const handleLoadingConfirm = async () => {
    setConfirmLoading(true);
    // 2초 후 완료 시뮬레이션
    setTimeout(() => {
      setConfirmLoading(false);
      setIsLoadingConfirmOpen(false);
      alert('작업이 완료되었습니다!');
    }, 2000);
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-2">Error Boundary 테스트 페이지</h1>
        <p className="text-gray-600">
          다양한 유형의 오류를 테스트하여 ErrorBoundary가 올바르게 동작하는지 확인합니다.
        </p>
      </div>

      <div className="space-y-6">
        <div className="border border-gray-200 rounded-lg p-4">
          <h2 className="text-lg font-semibold mb-4">1. 컴포넌트 렌더링 오류 테스트</h2>
          <p className="text-sm text-gray-600 mb-4">
            하단 버튼을 클릭하면 렌더링 오류가 발생하는 컴포넌트가 나타납니다.
          </p>
          <div className="mb-4">
            <Button
              onClick={() => setShowErrorComponent(!showErrorComponent)}
              variant={showErrorComponent ? "danger" : "default"}
            >
              {showErrorComponent ? "오류 컴포넌트 숨기기" : "오류 컴포넌트 보이기"}
            </Button>
          </div>
          {showErrorComponent && <ErrorBoundaryTest />}
        </div>

        <div className="border border-gray-200 rounded-lg p-4">
          <h2 className="text-lg font-semibold mb-4">2. 즉시 렌더링 오류</h2>
          <p className="text-sm text-gray-600 mb-4">
            이 버튼을 클릭하면 현재 페이지에서 즉시 렌더링 오류가 발생합니다.
          </p>
          <Button
            onClick={() => {
              // 강제로 컴포넌트 상태를 깨뜨려서 다음 렌더링에서 오류 발생
              throw new Error('즉시 발생한 렌더링 오류 - TestErrorPage');
            }}
            variant="danger"
          >
            즉시 렌더링 오류 발생
          </Button>
        </div>

        <div className="border border-gray-200 rounded-lg p-4">
          <h2 className="text-lg font-semibold mb-4">3. API 오류 시뮬레이션</h2>
          <p className="text-sm text-gray-600 mb-4">
            API 호출 오류를 시뮬레이션합니다 (콘솔 확인).
          </p>
          <Button
            onClick={async () => {
              try {
                const response = await fetch('/api/nonexistent-endpoint');
                if (!response.ok) {
                  throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                }
              } catch (error) {
                logger.error('API 오류 시뮬레이션:', error);
                alert('API 오류가 발생했습니다. 콘솔을 확인하세요.');
              }
            }}
            variant="warning"
          >
            API 오류 시뮬레이션
          </Button>
        </div>

        <div className="border border-gray-200 rounded-lg p-4">
          <h2 className="text-lg font-semibold mb-4">4. Modal 컴포넌트 테스트</h2>
          <p className="text-sm text-gray-600 mb-4">
            새로 구현된 Modal 컴포넌트의 다양한 기능을 테스트합니다.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <h3 className="font-medium text-sm">기본 Modal</h3>
              <Button
                onClick={() => setIsBasicModalOpen(true)}
                variant="default"
                className="w-full"
              >
                기본 Modal 열기
              </Button>
            </div>
            
            <div className="space-y-2">
              <h3 className="font-medium text-sm">확인 Dialog</h3>
              <Button
                onClick={() => setIsConfirmModalOpen(true)}
                variant="secondary"
                className="w-full"
              >
                확인 Modal 열기
              </Button>
            </div>
            
            <div className="space-y-2">
              <h3 className="font-medium text-sm">로딩 상태</h3>
              <Button
                onClick={() => setIsLoadingConfirmOpen(true)}
                variant="warning"
                className="w-full"
              >
                로딩 Modal 열기
              </Button>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-blue-800 mb-2">📝 테스트 가이드</h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• 렌더링 오류: ErrorBoundary에 의해 캐치되어 fallback UI 표시</li>
            <li>• API 오류: ErrorBoundary로 캐치되지 않으므로 별도 처리 필요</li>
            <li>• Modal: ESC 키, 배경 클릭, 포커스 관리, 스크롤 방지 기능 테스트</li>
            <li>• ConfirmModal: 로딩 상태, 버튼 변형, 콜백 처리 테스트</li>
            <li>• 개발 모드에서는 상세한 오류 정보 표시</li>
            <li>• 프로덕션에서는 사용자 친화적인 메시지만 표시</li>
          </ul>
        </div>
      </div>

      {/* Modal Components */}
      <Modal
        isOpen={isBasicModalOpen}
        onClose={() => setIsBasicModalOpen(false)}
        size="default"
      >
        <div className="p-6">
          <h2 className="text-xl font-semibold mb-4">기본 Modal 테스트</h2>
          <p className="text-gray-600 mb-6">
            이것은 기본 Modal 컴포넌트입니다. ESC 키를 누르거나 배경을 클릭하여 닫을 수 있습니다.
          </p>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setIsBasicModalOpen(false)}>
              취소
            </Button>
            <Button onClick={() => setIsBasicModalOpen(false)}>
              확인
            </Button>
          </div>
        </div>
      </Modal>

      <ConfirmModal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        title="작업 확인"
        message="이 작업을 실행하시겠습니까? 실행 후에는 되돌릴 수 없습니다."
        confirmText="실행"
        cancelText="취소"
        confirmVariant="danger"
        onConfirm={handleConfirm}
      />

      <ConfirmModal
        isOpen={isLoadingConfirmOpen}
        onClose={() => setIsLoadingConfirmOpen(false)}
        title="시간이 오래 걸리는 작업"
        message="이 작업은 시간이 오래 걸릴 수 있습니다. 계속 진행하시겠습니까?"
        confirmText="진행"
        cancelText="취소"
        confirmVariant="warning"
        loading={confirmLoading}
        onConfirm={handleLoadingConfirm}
      />
    </div>
  );
};